import { apiClient } from '../client';

/**
 * Client for `/api/eos/people-analyzer/*`. Stays in lockstep with
 * `server/src/routes/eos/peopleAnalyzer.routes.ts` (Stream B Wave 3,
 * ADR-023).
 *
 * Privacy (locked): rounds and raw scores are leadership-only
 * (manage_eos/superadmin org-wide, department leads scoped to their own
 * department's subjects). The person being reviewed reads their own
 * results via `/me` — closed rounds only, raters anonymized.
 */

export type CoreValueRating = 'plus' | 'plus_minus' | 'minus';
export type PaRoundStatus = 'open' | 'closed';

// ── Rounds ──────────────────────────────────────────────────────────────

export interface PaRound {
  id: string;
  organizationId: string;
  label: string;
  /** ISO datetime of the quarter boundary, or null (no quarter). */
  quarterStart: string | null;
  /** Null = org-wide round. */
  departmentId: string | null;
  status: PaRoundStatus;
  createdByMembershipId: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Leadership-only — plain members get a 403 (they use `getMyResults`). */
export async function listRounds(): Promise<PaRound[]> {
  const res = await apiClient.get<{ data: PaRound[] }>('/api/eos/people-analyzer/rounds');
  return res.data.data;
}

export interface CreateRoundRequest {
  label: string;
  /** YYYY-MM-DD quarter boundary. */
  quarterStart?: string;
  departmentId?: string;
}

export async function createRound(body: CreateRoundRequest): Promise<PaRound> {
  const res = await apiClient.post<{ data: PaRound }>('/api/eos/people-analyzer/rounds', body);
  return res.data.data;
}

/** Idempotent — closing a closed round is a 200 no-op. */
export async function closeRound(id: string): Promise<PaRound> {
  const res = await apiClient.post<{ data: PaRound }>(
    `/api/eos/people-analyzer/rounds/${encodeURIComponent(id)}/close`,
  );
  return res.data.data;
}

// ── Scores ──────────────────────────────────────────────────────────────

export interface CoreValueScore {
  value: string;
  rating: CoreValueRating;
}

/** Raw score row — what the upsert returns (no membership joins). */
export interface PaScoreRow {
  id: string;
  roundId: string;
  organizationId: string;
  subjectMembershipId: string;
  ratedByMembershipId: string;
  /** `CoreValueScore[]` snapshotted at review time — parse defensively. */
  coreValuesJson: unknown;
  getsIt: boolean | null;
  wantsIt: boolean | null;
  capacity: boolean | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Score as the round read returns it — subject + rater names joined. */
export type PaScore = PaScoreRow & {
  subjectMembership: {
    id: string;
    departmentId: string | null;
    user: { firstName: string; lastName: string };
  };
  ratedByMembership: { id: string; user: { firstName: string; lastName: string } };
};

/** Defensive reader for the `coreValuesJson` snapshot. */
export function parseCoreValues(raw: unknown): CoreValueScore[] {
  if (!Array.isArray(raw)) return [];
  const out: CoreValueScore[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const { value, rating } = item as Record<string, unknown>;
    if (typeof value !== 'string') continue;
    if (rating !== 'plus' && rating !== 'plus_minus' && rating !== 'minus') continue;
    out.push({ value, rating });
  }
  return out;
}

export async function listRoundScores(roundId: string, subjectMembershipId?: string): Promise<PaScore[]> {
  const params = new URLSearchParams();
  if (subjectMembershipId) params.set('subjectMembershipId', subjectMembershipId);
  const query = params.toString() ? `?${params.toString()}` : '';
  const res = await apiClient.get<{ data: PaScore[] }>(
    `/api/eos/people-analyzer/rounds/${encodeURIComponent(roundId)}/scores${query}`,
  );
  return res.data.data;
}

export interface UpsertScoreRequest {
  subjectMembershipId: string;
  /** Core values + ratings snapshotted from the CURRENT company V/TO. */
  coreValues: CoreValueScore[];
  getsIt?: boolean | null;
  wantsIt?: boolean | null;
  capacity?: boolean | null;
  notes?: string;
}

/**
 * One score per (round, subject, rater) — the rater is always the
 * caller; re-submitting revises your own review.
 */
export async function upsertScore(roundId: string, body: UpsertScoreRequest): Promise<PaScoreRow> {
  const res = await apiClient.put<{ data: PaScoreRow }>(
    `/api/eos/people-analyzer/rounds/${encodeURIComponent(roundId)}/scores`,
    body,
  );
  return res.data.data;
}

// ── /me — own results, closed rounds only, raters anonymized ────────────

export interface GwcCounts {
  yes: number;
  no: number;
  unanswered: number;
}

export interface MyRoundResult {
  roundId: string;
  roundLabel: string;
  closedAt: string | null;
  /** Each rater's core-values snapshot (`CoreValueScore[]`), identity stripped. */
  coreValues: unknown[];
  gwc: { getsIt: GwcCounts; wantsIt: GwcCounts; capacity: GwcCounts };
  /** Anonymous free-text notes. */
  notes: string[];
}

export async function getMyResults(): Promise<MyRoundResult[]> {
  const res = await apiClient.get<{ data: MyRoundResult[] }>('/api/eos/people-analyzer/me');
  return res.data.data;
}
